<script setup lang="ts">
export type DropdownOption = {
    value: string | number
    name: string
    image?: string
}

export interface FormSelectSimpleProps {
    id: string
    label?: string
    options: Array<DropdownOption>
    position?: 'left' | 'right'
    type?: 'primary' | 'secondary' | 'tertiary'
    showLabel?: boolean
    defaultValue?: DropdownOption
}

export interface DropdownProps {
    id: string
    label: string
    options: Array<DropdownOption>
}

export interface FormSelectSimpleEmits {
    // eslint-disable-next-line no-unused-vars
    (e: 'update', value: any): void
}

const props = withDefaults(defineProps<FormSelectSimpleProps>(), {
    position: 'left',
    type: 'primary',
    showLabel: false,
    label: '',
    defaultValue: undefined,
})

const expanded = ref<boolean>(false)
const current = ref<DropdownOption>(props.defaultValue ?? props.options[0] ?? { name: '', value: '' })
const label = ref<string>(props.label)
const button = ref<HTMLElement>()
const emit = defineEmits<FormSelectSimpleEmits>()

const handleClickOnOption = (item: DropdownOption): void => {
    // update select state
    current.value = item
    label.value = item.name
    expanded.value = false
    // emit value
    emit('update', item.value)
}

const handleClickOutside = (e: Event): void => {
    if (e.target === button.value) return
    expanded.value = false
}

const handleFocusOut = (e: FocusEvent): void => {
    // Close dropdown when focus leaves the component
    const wrapper = document.getElementById(props.id)
    if (wrapper && !wrapper.contains(e.relatedTarget as Node)) {
        expanded.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    const wrapper = document.getElementById(props.id)
    wrapper?.addEventListener('focusout', handleFocusOut)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    const wrapper = document.getElementById(props.id)
    wrapper?.removeEventListener('focusout', handleFocusOut)
})
</script>

<template>
  <div
    :id="props.id"
    class="simple-select-wrapper position-relative py-2 rounded-3"
    :class="`simple-select-wrapper-${props.type}`"
  >
    <input
      type="hidden"
      :aria-label="props.label"
      :name="props.id"
      :value="current?.value"
    >
    <div
      v-if="props.showLabel"
      class="simple-select-label text-black d-block mb-1 px-3 fs-xsmall text-black-40"
    >
      {{ props.label }}
    </div>
    <button
      :id="`dropdown-${props.id}`"
      ref="button"
      :aria-expanded="expanded"
      aria-haspopup="listbox"
      class="simple-select px-3 pt-0 pb-0 pe-6 stretched-link"
      :class="`simple-select-${props.type}`"
      type="button"
      @click="expanded = !expanded"
    >
      <span
        class="d-flex align-items-center text-start text-nowrap pe-none"
      >
        <span
          class="text-black text-nowrap overflow-hidden text-truncate"
        >
          {{ current.name }}
        </span>
      </span>
    </button>
    <DropdownDecor
      class="position-absolute top-50 me-4 end-0 translate-middle-y"
    />
    <Card
      :class="`${
        !expanded ? 'd-none' : ''
      } select-menu p-3 mt-3 border border-bcolor position-absolute end-0 z-index-5 ${
        position === 'left' ? 'start-0' : 'end-0'
      }`"
      background-color="white"
      box-shadow="4"
      :aria-labelledby="`dropdown-${props.id}`"
      @click="(e: Event) => e.stopPropagation()"
    >
      <ul
        role="listbox"
        class="select-menu-list"
        :aria-label="props.label"
      >
        <li
          v-for="(item, index) in props.options"
          :key="`select-simple-${props.id}-${index}`"
          role="option"
          :aria-selected="current?.value === item.value"
        >
          <img
            v-if="item.image"
            loading="lazy"
            decoding="async"
            :src="item.image"
            width="18"
            height="18"
            alt=""
            class="align-bottom me-2"
          >
          <button
            type="button"
            class="select-btn text-black d-flex align-items-center w-100 text-start pt-2 pb-2 fs-smaller p-0 border-0"
            @click="() => handleClickOnOption(item)"
          >
            {{ item.name }}
          </button>
        </li>
      </ul>
    </Card>
  </div>
</template>

<style scoped lang="scss">
@import './FormSelectSimple.scss';
</style>
